import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { OrgProvider, useOrg } from "@/context/OrgContext";
import { AppLayout } from "@/components/AppLayout";
import { Logo } from "@/components/Logo";
import { PageLoading } from "@/components/common/Loading";
import { Toaster } from "@/components/ui/sonner";

import Login from "@/pages/Login";
import SetPassword from "@/pages/SetPassword";
import Dashboard from "@/pages/Dashboard";
import Plan from "@/pages/Plan";
import PlanDetail from "@/pages/PlanDetail";
import WeeklyFocus from "@/pages/WeeklyFocus";
import Actions from "@/pages/Actions";
import Scorecard from "@/pages/Scorecard";
import Coaching from "@/pages/Coaching";
import CoachingDetail from "@/pages/CoachingDetail";
import BusinessHealth from "@/pages/BusinessHealth";
import Issues from "@/pages/Issues";
import Resources from "@/pages/Resources";
import AskAmden from "@/pages/AskAmden";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import Portfolio from "@/pages/Portfolio";
import Clients from "@/pages/Clients";
import Users from "@/pages/Users";
import AuditLog from "@/pages/AuditLog";
import AmdenTeam from "@/pages/AmdenTeam";
import MyWorkspace from "@/pages/MyWorkspace";
import TeamManagement from "@/pages/TeamManagement";
import AcceptInvite from "@/pages/AcceptInvite";
import { ModuleGate } from "@/components/ModuleGate";
import { WorkspaceGate } from "@/components/WorkspaceGate";
import { isWorkspaceOnly } from "@/lib/workspace";

function LoadingScreen() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#0B1729]">
      <Logo variant="dark" />
      <div className="mt-6 flex gap-1.5">
        <span className="h-2 w-2 animate-bounce rounded-full bg-[#FFB000]" />
        <span className="h-2 w-2 animate-bounce rounded-full bg-[#FFB000]" style={{ animationDelay: "0.15s" }} />
        <span className="h-2 w-2 animate-bounce rounded-full bg-[#FFB000]" style={{ animationDelay: "0.3s" }} />
      </div>
    </div>
  );
}

function RequireOrg({ children }) {
  const { orgId, loading } = useOrg();
  const { isAmden } = useAuth();
  if (loading) return <PageLoading />;
  if (!orgId) {
    if (isAmden) return <Navigate to="/clients" replace />;
    return <PageLoading />;
  }
  return children;
}

const myWorkspace = <WorkspaceGate need="self"><MyWorkspace /></WorkspaceGate>;

function AuthedApp() {
  const { user } = useAuth();
  if (isWorkspaceOnly(user)) {
    return (
      <OrgProvider>
        <AppLayout>
          <Routes>
            <Route path="/workspace" element={myWorkspace} />
            <Route path="*" element={<Navigate to="/workspace" replace />} />
          </Routes>
        </AppLayout>
      </OrgProvider>
    );
  }
  return (
    <OrgProvider>
      <AppLayout>
        <Routes>
          <Route path="/workspace" element={myWorkspace} />
          <Route path="/team" element={<WorkspaceGate need="team"><TeamManagement /></WorkspaceGate>} />
          <Route path="/portfolio" element={<Portfolio />} />
          <Route path="/clients" element={<Clients />} />
          <Route path="/users" element={<Users />} />
          <Route path="/amden-team" element={<AmdenTeam />} />
          <Route path="/resources" element={<ModuleGate moduleKey="resources" staffNoOrgOk><Resources /></ModuleGate>} />
          <Route path="/audit" element={<AuditLog />} />

          <Route path="/" element={<RequireOrg><Dashboard /></RequireOrg>} />
          <Route path="/plan" element={<ModuleGate moduleKey="plan"><Plan /></ModuleGate>} />
          <Route path="/plan/:id" element={<ModuleGate moduleKey="plan"><PlanDetail /></ModuleGate>} />
          <Route path="/weekly-focus" element={<ModuleGate moduleKey="weekly_focus"><WeeklyFocus /></ModuleGate>} />
          <Route path="/actions" element={<ModuleGate moduleKey="actions"><Actions /></ModuleGate>} />
          <Route path="/scorecard" element={<ModuleGate moduleKey="scorecard"><Scorecard /></ModuleGate>} />
          <Route path="/coaching" element={<ModuleGate moduleKey="coaching"><Coaching /></ModuleGate>} />
          <Route path="/coaching/:id" element={<ModuleGate moduleKey="coaching"><CoachingDetail /></ModuleGate>} />
          <Route path="/business-health" element={<ModuleGate moduleKey="business_health"><BusinessHealth /></ModuleGate>} />
          <Route path="/issues" element={<ModuleGate moduleKey="issues"><Issues /></ModuleGate>} />
          <Route path="/ask-amden" element={<ModuleGate moduleKey="ask_amden"><AskAmden /></ModuleGate>} />
          <Route path="/reports" element={<ModuleGate moduleKey="reports"><Reports /></ModuleGate>} />
          <Route path="/settings" element={<RequireOrg><Settings /></RequireOrg>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppLayout>
    </OrgProvider>
  );
}

function Root() {
  const { user, ready } = useAuth();
  if (!ready) return <LoadingScreen />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/set-password" element={<SetPassword />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/*" element={user ? <AuthedApp /> : <Navigate to="/login" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Root />
      </BrowserRouter>
    </AuthProvider>
  );
}
